/** Normalize addon size_pricing from API / form. */
export function normalizeAddonSizePricing(raw, fallbackPrice = 0) {
  const sp = raw && typeof raw === "object" ? raw : {};
  const allSizes = sp.all_sizes !== false && sp.all_sizes !== 0 && String(sp.all_sizes).toLowerCase() !== "false";
  const bySizeIn = sp.by_size && typeof sp.by_size === "object" ? sp.by_size : {};
  const by_size = {};
  for (const [key, row] of Object.entries(bySizeIn)) {
    const sizeKey = String(key || "").trim();
    if (!sizeKey) continue;
    const enabled =
      row?.enabled !== false &&
      row?.enabled !== 0 &&
      String(row?.enabled || "").toLowerCase() !== "false";
    let price = Number(row?.price);
    if (!Number.isFinite(price)) price = Number(fallbackPrice) || 0;
    by_size[sizeKey] = { enabled, price: Math.max(0, price) };
  }
  return { all_sizes: allSizes, by_size };
}

export function addonRowFromApi(addon) {
  const sp = normalizeAddonSizePricing(addon?.size_pricing, addon?.price);
  return {
    id: String(addon?.id || "").trim() || createAddonId(),
    name: String(addon?.name || ""),
    price: addon?.price != null && addon?.price !== "" ? String(addon.price) : "",
    all_sizes: sp.all_sizes,
    by_size: Object.fromEntries(
      Object.entries(sp.by_size).map(([k, v]) => [k, { ...v, price: String(v.price ?? "") }])
    ),
  };
}

export function createAddonId() {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `addon_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export function createAddonRow(name = "", price = "") {
  return {
    id: createAddonId(),
    name: String(name || ""),
    price: price === "" || price == null ? "" : String(price),
    all_sizes: true,
    by_size: {},
  };
}

function sizeKeysMatch(a, b) {
  return String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();
}

/** Resolve display/checkout price for an addon given selected product size. */
export function resolveAddonPriceForSize(addon, selectedSize, productHasSizes) {
  if (!addon) return null;
  const sp = normalizeAddonSizePricing(addon.size_pricing, addon.price);
  const base = Number(addon.price);
  const basePrice = Number.isFinite(base) ? base : 0;

  if (!productHasSizes) {
    return basePrice;
  }

  const size = String(selectedSize || "").trim();
  if (!size) {
    return sp.all_sizes ? basePrice : null;
  }

  if (sp.all_sizes) {
    return basePrice;
  }

  for (const [key, row] of Object.entries(sp.by_size || {})) {
    if (sizeKeysMatch(key, size) && row?.enabled !== false) {
      const p = Number(row.price);
      return Number.isFinite(p) ? Math.max(0, p) : basePrice;
    }
  }
  return null;
}

/** Whether addon should appear for the currently selected size. */
export function isAddonAvailableForSize(addon, selectedSize, productHasSizes) {
  return resolveAddonPriceForSize(addon, selectedSize, productHasSizes) != null;
}

export function serializeAddonRowForApi(row) {
  const name = String(row?.name || "").trim();
  if (!name) return null;

  const allSizes = row?.all_sizes !== false;
  let price = 0;
  const by_size = {};

  if (allSizes) {
    const p = Number(row?.price);
    price = Number.isFinite(p) ? Math.max(0, p) : 0;
  } else {
    let minPrice = null;
    for (const [sizeKey, cfg] of Object.entries(row?.by_size || {})) {
      const sk = String(sizeKey || "").trim();
      if (!sk) continue;
      const enabled =
        cfg?.enabled !== false &&
        cfg?.enabled !== 0 &&
        String(cfg?.enabled || "").toLowerCase() !== "false";
      const p = Number(cfg?.price);
      const pr = Number.isFinite(p) ? Math.max(0, p) : 0;
      by_size[sk] = { enabled, price: pr };
      if (enabled) {
        minPrice = minPrice == null ? pr : Math.min(minPrice, pr);
      }
    }
    price = minPrice != null ? minPrice : 0;
  }

  const out = {
    id: String(row?.id || "").trim() || undefined,
    name,
    price: Math.round(price * 100) / 100,
    size_pricing: allSizes
      ? { all_sizes: true }
      : { all_sizes: false, by_size },
  };
  if (!out.id) delete out.id;
  return out;
}

export function categoryPillCircleSizePx(viewportWidth) {
  const vw =
    typeof viewportWidth === "number"
      ? viewportWidth
      : typeof window !== "undefined"
        ? window.innerWidth
        : 390;
  return Math.min(84, Math.max(64, vw * 0.16));
}

export function categoryCircleFontSizePx(label, circlePx = 84) {
  const text = String(label || "").trim();
  if (!text) return 10;
  const ring = Number(circlePx) || 84;
  const pad = ring * 0.12;
  const inner = ring - pad * 2;
  const words = text.split(/\s+/).filter(Boolean);
  const lineCount = words.length > 1 ? words.length : Math.max(1, Math.ceil(text.length / 6));
  const longestLine =
    words.length > 1 ? Math.max(...words.map((w) => w.length), 1) : text.length;
  const lineHeight = 1.1;
  const sizeByHeight = inner / (lineCount * lineHeight);
  const sizeByWidth = inner / (longestLine * 0.58);
  let size = Math.min(sizeByHeight, sizeByWidth) * 0.92;
  if (text.length > 14) size *= 0.96;
  if (text.length > 20) size *= 0.94;
  return Math.min(12, Math.max(6.5, Math.round(size * 10) / 10));
}

export function categoryCircleGlyphMeta(label, circlePx) {
  const text = String(label || "").trim();
  const words = text.split(/\s+/).filter(Boolean);
  const len = text.length;
  const ring = Number(circlePx) || categoryPillCircleSizePx();
  const fontSize = categoryCircleFontSizePx(text, ring);
  let glyphClass = "";
  if (len > 16) glyphClass = "category-pill__circle-glyph--tiny";
  else if (len > 10 || words.length > 2) glyphClass = "category-pill__circle-glyph--compact";
  else if (len > 6 || words.length > 1) glyphClass = "category-pill__circle-glyph--medium";
  const lineHeight = words.length > 2 ? 1.05 : words.length > 1 ? 1.08 : 1.12;
  return { words: words.length ? words : [text], fontSize, lineHeight, glyphClass, circlePx: ring };
}
